import {useEffect, useMemo, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {onDesktop} from "@pg-design/helpers-css";
import {InputRange, InputWrapper, IRange} from "@pg-design/inputs-module";
import {Text} from "@pg-design/text-module";
import {form, IFormProps} from "@pg-mono/form/index";
import {FILTERS_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {Currency, OfferType} from "@pg-rp/common-types";

import {formatBigNumbers} from "../credit_calculator/utils/format_big_numbers";
import {useClickOutside} from "../hooks/handle_click_outside";
import {
    FilterCategory,
    getMobileFiltersLabelForTracking,
    trackFilterApply,
    trackFilterApplyForCategories,
    trackFilterClear,
    trackFilterClearForCategories,
    trackFilterOpen,
    trackFilterRemove
} from "../offer/tracking/hit_offer_modal_filters";
import {BudgetFieldsNames} from "../real_estate/types/BudgetFieldsNames";
import {errorMessages, hasAreaValidationError} from "../real_estate/utils/has_area_validation_error";
import {Filter} from "../search/components/filters/SearchFilter";
import {FilterFooter} from "../search/components/filters/SearchFiltersFooter";
import {SearchFiltersRooms} from "../search/components/filters/SearchFiltersRooms";
import {IFilterChildProps, RangeFilter} from "../search/components/filters/SearchRangeFilter";
import {FloorChoicesSwitch} from "../search/components/FloorChoicesSwitch";
import {DropdownToOpen} from "../search/constants/DropdownToOpen";
import {getAreaFilterLimitedValues} from "../search/utils/get_area_filter_proper_value";
import {getAreaFilterLabel, getFloorChoiceLabel} from "../search/utils/get_label";
import {getRoomLabel} from "../search/utils/room_field_data_translations";
import {PREFERENCES_FORM_MAX_DEFAULT} from "../user/constants/preferences/application_preferences_form";
import {useUserSearchPreferences} from "../user/hooks/use_user_search_preferences";
import {IUserPreferencesFilters} from "../user/interfaces/IUserPreferences";
import {getCurrency} from "../utils/get_currency";
import {PropertyListFiltersBudget} from "./PropertyListFiltersBudget";

enum Round {
    PRICE = 10000,
    AREA = 5,
    PRICE_M2 = 500
}

interface IOwnProps {
    availableRooms?: number[];
    offerType?: OfferType;
    setDesktopFilterBlendActive?: (isOpen: boolean) => void;
    onMobileCloseModal?: () => void;
    onMobileResetForm?: () => void;
    noWrapper?: boolean;
    className?: string;
    currency: Currency;
    maxPriceInOffer?: number;
}

export interface ISearchFiltersFormValues {
    area: IRange<string>;
    price: IRange<string>;
    rooms: IRange<number | "">;
    floor_choices: number[];
    house_storeys: number[];
}

interface IProps extends IOwnProps, IFormProps<ISearchFiltersFormValues> {}

const SearchFiltersC = (props: IProps) => {
    const {isMobile} = useUserDevice();
    /**
     * Ref's
     */
    const filtersWrapperRef = useRef<HTMLElement | null>(null);
    const filtersWrapperRefHandle = (elem: HTMLElement | null) => {
        filtersWrapperRef.current = elem;
    };

    const FilterWrapper = props.noWrapper ? NoWrapper : InputWrapper;

    /**
     * State
     */
    const [currentOpenFilter, setCurrentOpenFilter] = useState<DropdownToOpen | null | undefined>(null);

    /**
     * Utils
     */

    useClickOutside(filtersWrapperRef, () => {
        setCurrentOpenFilter(null);
        props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
    });

    /**
     * Mobile specific
     */

    const closeMobileModal = () => {
        setCurrentOpenFilter(null);
        props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
        props.onMobileCloseModal && props.onMobileCloseModal();
    };

    /**
     * General
     */

    const onDropdownOpen = (dropdownToOpen: DropdownToOpen | null) => {
        const shouldOpen = typeof dropdownToOpen === "number" && dropdownToOpen >= 0;

        if (shouldOpen) {
            props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(true);
            setCurrentOpenFilter(dropdownToOpen);

            // Track open event for desktop
            if (!isMobile) {
                trackFilterOpen(dropdownToOpen, "invest_filters");
            }
        } else {
            props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
            setCurrentOpenFilter(null);
        }
    };

    /**
     * Render
     */

    const areaProps = props.getFieldProps("area");
    const roomsProps = props.getFieldProps("rooms");
    const floorChoicesProps = props.getFieldProps("floor_choices");
    const budgetProps = props.getFieldProps("price");
    const houseStoreysProps = props.getFieldProps("house_storeys");

    const availableRooms = props.availableRooms;
    const areaErrorMessage = hasAreaValidationError(props.values.area) ? errorMessages.area : undefined;
    const offerType = props.offerType;
    const shouldShowFloorsFilter = offerType === OfferType.FLAT || offerType === OfferType.HOUSE;
    const isHouseFilter = offerType === OfferType.HOUSE;

    const {updateUserPreferences, userPreferences} = useUserSearchPreferences();
    const userPreferencesFilters = userPreferences?.data?.filters || {};

    const maxInOffer = props.maxPriceInOffer ? Math.ceil(props.maxPriceInOffer / 100000) * 100000 : 0;
    const parsedPrice = props.values.price.upper !== "" ? parseInt(props.values.price.upper) : 0;
    const maxBudget = Math.max(parsedPrice, maxInOffer, PREFERENCES_FORM_MAX_DEFAULT);
    const defaultBudgetValues = useMemo(
        () => ({
            [BudgetFieldsNames.RADIO]: "total",
            [BudgetFieldsNames.CREDIT]: 0,
            [BudgetFieldsNames.TOTAL]: !!parsedPrice ? parsedPrice : maxBudget
        }),
        [props.values.price]
    );

    const formMethods = useForm({defaultValues: defaultBudgetValues});

    const {handleSubmit, formState, reset} = formMethods;

    useEffect(() => {
        if (!(BudgetFieldsNames.TOTAL in formState.dirtyFields)) {
            reset(defaultBudgetValues);
        }
    }, [props.values.price]);

    const onFieldClear = (name?: string, isRemove: boolean = true) => {
        // Track remove event for desktop (only when clicking X, not when clicking Clear button)
        if (!isMobile && name && isRemove) {
            const trackingMap: Record<string, () => void> = {
                [budgetProps.name]: () =>
                    trackFilterRemove({
                        eventName: "invest_filters",
                        filterType: "budget",
                        value: budgetProps.value,
                        currency: props.currency
                    }),
                [roomsProps.name]: () =>
                    trackFilterRemove({
                        eventName: "invest_filters",
                        filterType: "rooms",
                        value: roomsProps.value
                    }),
                [areaProps.name]: () =>
                    trackFilterRemove({
                        eventName: "invest_filters",
                        filterType: "area",
                        value: areaProps.value
                    }),
                [floorChoicesProps.name]: () => {
                    const floorValue = isHouseFilter ? houseStoreysProps.value : floorChoicesProps.value;
                    trackFilterRemove({
                        eventName: "invest_filters",
                        filterType: "floor",
                        value: floorValue,
                        offerType
                    });
                },
                [houseStoreysProps.name]: () => {
                    const floorValue = isHouseFilter ? houseStoreysProps.value : floorChoicesProps.value;
                    trackFilterRemove({
                        eventName: "invest_filters",
                        filterType: "floor",
                        value: floorValue,
                        offerType
                    });
                }
            };

            const fn = trackingMap[name];
            if (fn) fn();
        }

        const cleanMap: Record<string, Partial<IUserPreferencesFilters>> = {
            [areaProps.name]: {area_0: undefined, area_1: undefined},
            [roomsProps.name]: {rooms_0: undefined, rooms_1: undefined},
            [floorChoicesProps.name]: {floor_choices: undefined},
            [budgetProps.name]: {price_1: undefined},
            [houseStoreysProps.name]: {house_storeys: undefined}
        };

        let filters = {...userPreferencesFilters};

        if (name && cleanMap[name]) {
            filters = {...filters, ...cleanMap[name]};
        } else {
            filters = {
                ...filters,
                area_0: undefined,
                area_1: undefined,
                rooms_0: undefined,
                rooms_1: undefined,
                floor_choices: undefined,
                price_1: undefined,
                house_storeys: undefined
            };
        }

        updatePreferences(filters);
    };

    const updatePreferences = (filters: IUserPreferencesFilters) => {
        updateUserPreferences(filters);
    };

    const onRHFSubmit = (value: typeof defaultBudgetValues) => {
        if (hasAreaValidationError(areaProps.value)) {
            return;
        }

        const newBudget = value[BudgetFieldsNames.TOTAL];
        const newBudgetValue = {lower: "", upper: newBudget >= maxBudget ? "" : newBudget.toString()};

        // GTM Tracking for budget apply (with new value from form)
        if (!isMobile) {
            trackFilterApply({
                eventName: "invest_filters",
                filterType: "budget",
                value: newBudgetValue,
                currency: props.currency
            });
        }

        // we have to update redux form to trigger filters change in parent component
        budgetProps.onChange(budgetProps.name, newBudgetValue);
        // onAfterChange from redux form triggers list fetch
        budgetProps.onAfterChange?.(budgetProps.name, newBudgetValue);

        updatePreferences({
            price_1: newBudget.toString(),
            floor_choices: floorChoicesProps.value.map((el) => el.toString()),
            house_storeys: houseStoreysProps.value.map((el) => el.toString()),
            ...(areaProps.value.lower !== "" ? {area_0: areaProps.value.lower} : {area_0: undefined}),
            ...(areaProps.value.upper !== "" ? {area_1: areaProps.value.upper} : {area_1: undefined}),
            ...(typeof roomsProps.value.lower !== "string" && roomsProps.value.lower !== 0
                ? {rooms_0: roomsProps.value.lower.toString()}
                : {rooms_0: undefined}),
            ...(typeof roomsProps.value.lower !== "string" && roomsProps.value.lower !== 0
                ? {rooms_1: roomsProps.value.lower.toString()}
                : {rooms_1: undefined})
        });
        if (isMobile) {
            closeMobileModal();
        } else {
            onDropdownOpen(null);
        }
    };

    return (
        <FiltersHolder shouldShowFloorsFilter={shouldShowFloorsFilter} ref={filtersWrapperRefHandle} className={props.className}>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onRHFSubmit)}>
                    <div css={filtersScroller}>
                        <FilterWrapper css={inputWrapperStyles}>
                            <RangeFilter
                                id="budgetFilter"
                                getLabel={(value, defaultLabel) =>
                                    value.upper && parseInt(value.upper) < maxBudget
                                        ? `do ${formatBigNumbers(parseInt(value.upper))} ${getCurrency({
                                              currency: props.currency
                                          })}`
                                        : defaultLabel
                                }
                                label="Budżet"
                                isDropdownOpened={currentOpenFilter === DropdownToOpen.PRICE}
                                dropdownName={DropdownToOpen.PRICE}
                                labelId="budget-filter-field"
                                {...budgetProps}
                                onClearField={() => {
                                    onFieldClear(budgetProps.name);
                                }}
                                errorOnBottom
                                toggleDropdown={onDropdownOpen}
                                isMobileWidth={!!isMobile}
                            >
                                {(filterChildProps) => {
                                    return (
                                        <PropertyListFiltersBudget
                                            label={filterChildProps.label}
                                            onClear={() => {
                                                if (!isMobile) {
                                                    trackFilterClear({
                                                        eventName: "invest_filters",
                                                        filterType: "budget",
                                                        value: budgetProps.value,
                                                        currency: props.currency
                                                    });
                                                }
                                                // Clear field without triggering Remove event
                                                filterChildProps.onChange(filterChildProps.name, {lower: "", upper: ""});
                                                filterChildProps.onAfterChange?.(filterChildProps.name, {lower: "", upper: ""});
                                                // Reset form to default values
                                                reset(defaultBudgetValues);
                                                // Update preferences to clear budget
                                                updatePreferences({
                                                    price_1: undefined
                                                });
                                            }}
                                            closeForm={() => {
                                                filterChildProps.toggleDropdown();
                                            }}
                                            onSubmit={handleSubmit(onRHFSubmit)}
                                            maxPrice={maxBudget}
                                        />
                                    );
                                }}
                            </RangeFilter>
                        </FilterWrapper>

                        <FilterWrapper css={inputWrapperStyles}>
                            <RangeFilter
                                label="Pokoje"
                                id="roomFilter"
                                {...roomsProps}
                                onClearField={() => {
                                    onFieldClear(roomsProps.name);
                                }}
                                getLabel={getRoomLabel}
                                labelId="room-filter-field"
                                errorOnBottom
                                isDropdownOpened={currentOpenFilter === DropdownToOpen.ROOMS}
                                toggleDropdown={onDropdownOpen}
                                dropdownName={DropdownToOpen.ROOMS}
                                isMobileWidth={!!isMobile}
                                dataTestId={FILTERS_TEST_IDS.ROOMS}
                            >
                                {(props: IFilterChildProps<number | "">) => (
                                    <SearchFiltersRooms
                                        rooms={availableRooms}
                                        {...props}
                                        onSubmitSingle={() => {
                                            if (!isMobile) {
                                                trackFilterApply({
                                                    eventName: "invest_filters",
                                                    filterType: "rooms",
                                                    value: props.value
                                                });
                                            }

                                            updatePreferences({
                                                rooms_0: typeof props.value.lower === "number" ? props.value.lower.toString() : undefined,
                                                rooms_1: typeof props.value.upper === "number" ? props.value.upper.toString() : undefined
                                            });
                                        }}
                                        clearField={() => {
                                            if (!isMobile) {
                                                trackFilterClear({
                                                    eventName: "invest_filters",
                                                    filterType: "rooms",
                                                    value: props.value
                                                });
                                            }

                                            props.onChange(props.name, {lower: "", upper: ""});
                                            props.onAfterChange?.(props.name, {lower: "", upper: ""});
                                        }}
                                        showFooter={!isMobile}
                                    />
                                )}
                            </RangeFilter>
                        </FilterWrapper>

                        <FilterWrapper css={inputWrapperStyles}>
                            <RangeFilter
                                label="Metraż"
                                errorOnBottom
                                id="areaFilter"
                                {...areaProps}
                                onClearField={() => {
                                    onFieldClear(areaProps.name);
                                }}
                                onChange={areaProps.onChange}
                                getLabel={getAreaFilterLabel}
                                isDropdownOpened={currentOpenFilter === DropdownToOpen.AREA}
                                toggleDropdown={onDropdownOpen}
                                dropdownName={DropdownToOpen.AREA}
                                isMobileWidth={!!isMobile}
                                dataTestId={FILTERS_TEST_IDS.AREA_SIZE}
                            >
                                {(props: IFilterChildProps<string>) => (
                                    <>
                                        <Text variant="body_copy_1" as="p" mb={2}>
                                            {props.label}
                                        </Text>
                                        <InputRange
                                            {...props}
                                            error={areaErrorMessage}
                                            onChange={(name, value) => {
                                                const limitedValues = getAreaFilterLimitedValues(value as IRange<string>);
                                                props.onChange(name, limitedValues);
                                            }}
                                            hasDashSeparator
                                            roundToMultiplicationOf={Round.AREA}
                                        />

                                        {!isMobile && (
                                            <FilterFooter
                                                onCancel={() => {
                                                    trackFilterClear({
                                                        eventName: "invest_filters",
                                                        filterType: "area",
                                                        value: props.value
                                                    });

                                                    props.onChange(props.name, {lower: "", upper: ""});
                                                    props.onAfterChange?.(props.name, {lower: "", upper: ""});
                                                    props.toggleDropdown();
                                                }}
                                                onSave={() => {
                                                    if (!hasAreaValidationError(props.value)) {
                                                        trackFilterApply({
                                                            eventName: "invest_filters",
                                                            filterType: "area",
                                                            value: props.value
                                                        });

                                                        props.onChange(props.name, props.value);
                                                        props.onAfterChange?.(props.name, props.value);

                                                        updatePreferences({
                                                            area_0: props.value.lower !== "" ? props.value.lower : undefined,
                                                            area_1: props.value.upper !== "" ? props.value.upper : undefined
                                                        });

                                                        props.toggleDropdown();
                                                    }
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </RangeFilter>
                        </FilterWrapper>

                        {shouldShowFloorsFilter && (
                            <FilterWrapper css={inputWrapperStyles}>
                                <Filter
                                    label="Piętra"
                                    id="floorChoicesFilter"
                                    {...(isHouseFilter ? houseStoreysProps : floorChoicesProps)}
                                    onClearField={() => {
                                        onFieldClear(isHouseFilter ? houseStoreysProps.name : floorChoicesProps.name);
                                    }}
                                    getLabel={(value) => getFloorChoiceLabel(value, offerType)}
                                    labelId={`${isHouseFilter ? "house_storeys" : "floor_choices"}-filter-field`}
                                    errorOnBottom
                                    isDropdownOpened={currentOpenFilter === DropdownToOpen.FLOORS}
                                    setDropdownOpen={onDropdownOpen}
                                    dropdownName={DropdownToOpen.FLOORS}
                                    isMobileWidth={!!isMobile}
                                    dataTestId={FILTERS_TEST_IDS.FLOORS}
                                >
                                    {(props) => (
                                        <>
                                            <FloorChoicesSwitch offerType={offerType} {...props} />

                                            {!isMobile && (
                                                <FilterFooter
                                                    onCancel={() => {
                                                        trackFilterClear({
                                                            eventName: "invest_filters",
                                                            filterType: "floor",
                                                            value: props.value,
                                                            offerType
                                                        });

                                                        props.onChange(props.name, []);
                                                        props.onAfterChange?.(props.name, []);

                                                        onDropdownOpen(null);
                                                    }}
                                                    onSave={() => {
                                                        const mappedValue = props.value.map((el) => el.toString());

                                                        trackFilterApply({
                                                            eventName: "invest_filters",
                                                            filterType: "floor",
                                                            value: props.value,
                                                            offerType
                                                        });

                                                        props.onChange(props.name, props.value);
                                                        props.onAfterChange?.(props.name, props.value);

                                                        updatePreferences(isHouseFilter ? {house_storeys: mappedValue} : {floor_choices: mappedValue});
                                                        onDropdownOpen(null);
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                </Filter>
                            </FilterWrapper>
                        )}
                    </div>

                    <div css={mobileFiltersFooterWrapper}>
                        <FilterFooter
                            onCancel={() => {
                                if (isMobile) {
                                    const {categories, options} = getMobileFiltersLabelForTracking(
                                        {
                                            price: budgetProps.value,
                                            rooms: roomsProps.value,
                                            area: areaProps.value,
                                            floor_choices: isHouseFilter ? undefined : floorChoicesProps.value,
                                            house_storeys: isHouseFilter ? houseStoreysProps.value : undefined
                                        },
                                        offerType,
                                        props.currency === Currency.EURO ? "eur" : "zł"
                                    );

                                    if (categories) {
                                        trackFilterClearForCategories({
                                            eventName: "invest_filters",
                                            filterCategory: categories as FilterCategory,
                                            customLabel: options || undefined
                                        });
                                    }
                                }

                                props.onMobileResetForm?.();
                                onFieldClear();
                            }}
                            onSave={(e) => {
                                if (isMobile) {
                                    const formValues = formMethods.getValues();
                                    const newBudget = formValues[BudgetFieldsNames.TOTAL];
                                    const newBudgetValue = {lower: "", upper: newBudget >= maxBudget ? "" : newBudget.toString()};

                                    handleSubmit(onRHFSubmit)(e);

                                    const {categories, options} = getMobileFiltersLabelForTracking(
                                        {
                                            price: newBudgetValue,
                                            rooms: roomsProps.value,
                                            area: areaProps.value,
                                            floor_choices: isHouseFilter ? undefined : floorChoicesProps.value,
                                            house_storeys: isHouseFilter ? houseStoreysProps.value : undefined
                                        },
                                        offerType,
                                        props.currency === Currency.EURO ? "eur" : "zł"
                                    );

                                    if (categories) {
                                        trackFilterApplyForCategories({
                                            eventName: "invest_filters",
                                            filterCategory: categories as FilterCategory,
                                            customLabel: options || undefined
                                        });
                                    }
                                } else {
                                    handleSubmit(onRHFSubmit)(e);
                                }
                            }}
                        />
                    </div>
                </form>
            </FormProvider>
        </FiltersHolder>
    );
};

export const PropertyListFilters = form<ISearchFiltersFormValues, IProps>(SearchFiltersC);

const FiltersHolder = styled.div<{shouldShowFloorsFilter: boolean}>`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 6rem);

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        height: unset;
    }
`;

const filtersScroller = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: auto;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-direction: row;
        overflow: initial;
    }
`;

const mobileFiltersFooterWrapper = (theme: Theme) => css`
    width: 100%;
    display: block;
    padding: 0 2rem 2rem;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const inputWrapperStyles = css`
    padding: 0;
    margin-right: 0.8rem;

    ${onDesktop(css`
        flex: 1 1 20%;
    `)}
`;

const NoWrapper = styled.div`
    display: flex;
`;
import {useEffect, useMemo, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {css, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {onDesktop} from "@pg-design/helpers-css";
import {InputRange, InputWrapper, IRange} from "@pg-design/inputs-module";
import {Text} from "@pg-design/text-module";
import {form, IFormProps} from "@pg-mono/form/index";
import {FILTERS_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";
import {Currency, OfferType} from "@pg-rp/common-types";

import {formatBigNumbers} from "../credit_calculator/utils/format_big_numbers";
import {useClickOutside} from "../hooks/handle_click_outside";
import {
    FilterCategory,
    getMobileFiltersLabelForTracking,
    trackFilterApply,
    trackFilterApplyForCategories,
    trackFilterClear,
    trackFilterClearForCategories,
    trackFilterOpen,
    trackFilterRemove
} from "../offer/tracking/hit_offer_modal_filters";
import {BudgetFieldsNames} from "../real_estate/types/BudgetFieldsNames";
import {errorMessages, hasAreaValidationError} from "../real_estate/utils/has_area_validation_error";
import {Filter} from "../search/components/filters/SearchFilter";
import {FilterFooter} from "../search/components/filters/SearchFiltersFooter";
import {SearchFiltersRooms} from "../search/components/filters/SearchFiltersRooms";
import {IFilterChildProps, RangeFilter} from "../search/components/filters/SearchRangeFilter";
import {FloorChoicesSwitch} from "../search/components/FloorChoicesSwitch";
import {DropdownToOpen} from "../search/constants/DropdownToOpen";
import {getAreaFilterLimitedValues} from "../search/utils/get_area_filter_proper_value";
import {getAreaFilterLabel, getFloorChoiceLabel} from "../search/utils/get_label";
import {getRoomLabel} from "../search/utils/room_field_data_translations";
import {PREFERENCES_FORM_MAX_DEFAULT} from "../user/constants/preferences/application_preferences_form";
import {useUserSearchPreferences} from "../user/hooks/use_user_search_preferences";
import {IUserPreferencesFilters} from "../user/interfaces/IUserPreferences";
import {getCurrency} from "../utils/get_currency";
import {PropertyListFiltersBudget} from "./PropertyListFiltersBudget";

enum Round {
    PRICE = 10000,
    AREA = 5,
    PRICE_M2 = 500
}

interface IOwnProps {
    availableRooms?: number[];
    offerType?: OfferType;
    setDesktopFilterBlendActive?: (isOpen: boolean) => void;
    onMobileCloseModal?: () => void;
    onMobileResetForm?: () => void;
    noWrapper?: boolean;
    className?: string;
    currency: Currency;
    maxPriceInOffer?: number;
}

export interface ISearchFiltersFormValues {
    area: IRange<string>;
    price: IRange<string>;
    rooms: IRange<number | "">;
    floor_choices: number[];
    house_storeys: number[];
}

interface IProps extends IOwnProps, IFormProps<ISearchFiltersFormValues> {}

const SearchFiltersC = (props: IProps) => {
    const {isMobile} = useUserDevice();
    /**
     * Ref's
     */
    const filtersWrapperRef = useRef<HTMLElement | null>(null);
    const filtersWrapperRefHandle = (elem: HTMLElement | null) => {
        filtersWrapperRef.current = elem;
    };

    const FilterWrapper = props.noWrapper ? NoWrapper : InputWrapper;

    /**
     * State
     */
    const [currentOpenFilter, setCurrentOpenFilter] = useState<DropdownToOpen | null | undefined>(null);

    /**
     * Utils
     */

    useClickOutside(filtersWrapperRef, () => {
        setCurrentOpenFilter(null);
        props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
    });

    /**
     * Mobile specific
     */

    const closeMobileModal = () => {
        setCurrentOpenFilter(null);
        props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
        props.onMobileCloseModal && props.onMobileCloseModal();
    };

    /**
     * General
     */

    const onDropdownOpen = (dropdownToOpen: DropdownToOpen | null) => {
        const shouldOpen = typeof dropdownToOpen === "number" && dropdownToOpen >= 0;

        if (shouldOpen) {
            props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(true);
            setCurrentOpenFilter(dropdownToOpen);

            // Track open event for desktop
            if (!isMobile) {
                trackFilterOpen(dropdownToOpen, "invest_filters");
            }
        } else {
            props.setDesktopFilterBlendActive && props.setDesktopFilterBlendActive(false);
            setCurrentOpenFilter(null);
        }
    };

    /**
     * Render
     */

    const areaProps = props.getFieldProps("area");
    const roomsProps = props.getFieldProps("rooms");
    const floorChoicesProps = props.getFieldProps("floor_choices");
    const budgetProps = props.getFieldProps("price");
    const houseStoreysProps = props.getFieldProps("house_storeys");

    const availableRooms = props.availableRooms;
    const areaErrorMessage = hasAreaValidationError(props.values.area) ? errorMessages.area : undefined;
    const offerType = props.offerType;
    const shouldShowFloorsFilter = offerType === OfferType.FLAT || offerType === OfferType.HOUSE;
    const isHouseFilter = offerType === OfferType.HOUSE;

    const {updateUserPreferences, userPreferences} = useUserSearchPreferences();
    const userPreferencesFilters = userPreferences?.data?.filters || {};

    const maxInOffer = props.maxPriceInOffer ? Math.ceil(props.maxPriceInOffer / 100000) * 100000 : 0;
    const parsedPrice = props.values.price.upper !== "" ? parseInt(props.values.price.upper) : 0;
    const maxBudget = Math.max(parsedPrice, maxInOffer, PREFERENCES_FORM_MAX_DEFAULT);
    const defaultBudgetValues = useMemo(
        () => ({
            [BudgetFieldsNames.RADIO]: "total",
            [BudgetFieldsNames.CREDIT]: 0,
            [BudgetFieldsNames.TOTAL]: !!parsedPrice ? parsedPrice : maxBudget
        }),
        [props.values.price]
    );

    const formMethods = useForm({defaultValues: defaultBudgetValues});

    const {handleSubmit, formState, reset} = formMethods;

    useEffect(() => {
        if (!(BudgetFieldsNames.TOTAL in formState.dirtyFields)) {
            reset(defaultBudgetValues);
        }
    }, [props.values.price]);

    const onFieldClear = (name?: string, isRemove: boolean = true) => {
        // Track remove event for desktop (only when clicking X, not when clicking Clear button)
        if (!isMobile && name && isRemove) {
            const trackingMap: Record<string, () => void> = {
                [budgetProps.name]: () =>
                    trackFilterRemove({
                        eventName: "invest_filters",
                        filterType: "budget",
                        value: budgetProps.value,
                        currency: props.currency
                    }),
                [roomsProps.name]: () =>
                    trackFilterRemove({
                        eventName: "invest_filters",
                        filterType: "rooms",
                        value: roomsProps.value
                    }),
                [areaProps.name]: () =>
                    trackFilterRemove({
                        eventName: "invest_filters",
                        filterType: "area",
                        value: areaProps.value
                    }),
                [floorChoicesProps.name]: () => {
                    const floorValue = isHouseFilter ? houseStoreysProps.value : floorChoicesProps.value;
                    trackFilterRemove({
                        eventName: "invest_filters",
                        filterType: "floor",
                        value: floorValue,
                        offerType
                    });
                },
                [houseStoreysProps.name]: () => {
                    const floorValue = isHouseFilter ? houseStoreysProps.value : floorChoicesProps.value;
                    trackFilterRemove({
                        eventName: "invest_filters",
                        filterType: "floor",
                        value: floorValue,
                        offerType
                    });
                }
            };

            const fn = trackingMap[name];
            if (fn) fn();
        }

        const cleanMap: Record<string, Partial<IUserPreferencesFilters>> = {
            [areaProps.name]: {area_0: undefined, area_1: undefined},
            [roomsProps.name]: {rooms_0: undefined, rooms_1: undefined},
            [floorChoicesProps.name]: {floor_choices: undefined},
            [budgetProps.name]: {price_1: undefined},
            [houseStoreysProps.name]: {house_storeys: undefined}
        };

        let filters = {...userPreferencesFilters};

        if (name && cleanMap[name]) {
            filters = {...filters, ...cleanMap[name]};
        } else {
            filters = {
                ...filters,
                area_0: undefined,
                area_1: undefined,
                rooms_0: undefined,
                rooms_1: undefined,
                floor_choices: undefined,
                price_1: undefined,
                house_storeys: undefined
            };
        }

        updatePreferences(filters);
    };

    const updatePreferences = (filters: IUserPreferencesFilters) => {
        updateUserPreferences(filters);
    };

    const onRHFSubmit = (value: typeof defaultBudgetValues) => {
        if (hasAreaValidationError(areaProps.value)) {
            return;
        }

        const newBudget = value[BudgetFieldsNames.TOTAL];
        const newBudgetValue = {lower: "", upper: newBudget >= maxBudget ? "" : newBudget.toString()};

        // GTM Tracking for budget apply (with new value from form)
        if (!isMobile) {
            trackFilterApply({
                eventName: "invest_filters",
                filterType: "budget",
                value: newBudgetValue,
                currency: props.currency
            });
        }

        // we have to update redux form to trigger filters change in parent component
        budgetProps.onChange(budgetProps.name, newBudgetValue);
        // onAfterChange from redux form triggers list fetch
        budgetProps.onAfterChange?.(budgetProps.name, newBudgetValue);

        updatePreferences({
            price_1: newBudget.toString(),
            floor_choices: floorChoicesProps.value.map((el) => el.toString()),
            house_storeys: houseStoreysProps.value.map((el) => el.toString()),
            ...(areaProps.value.lower !== "" ? {area_0: areaProps.value.lower} : {area_0: undefined}),
            ...(areaProps.value.upper !== "" ? {area_1: areaProps.value.upper} : {area_1: undefined}),
            ...(typeof roomsProps.value.lower !== "string" && roomsProps.value.lower !== 0
                ? {rooms_0: roomsProps.value.lower.toString()}
                : {rooms_0: undefined}),
            ...(typeof roomsProps.value.lower !== "string" && roomsProps.value.lower !== 0
                ? {rooms_1: roomsProps.value.lower.toString()}
                : {rooms_1: undefined})
        });
        if (isMobile) {
            closeMobileModal();
        } else {
            onDropdownOpen(null);
        }
    };

    return (
        <FiltersHolder shouldShowFloorsFilter={shouldShowFloorsFilter} ref={filtersWrapperRefHandle} className={props.className}>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onRHFSubmit)}>
                    <div css={filtersScroller}>
                        <FilterWrapper css={inputWrapperStyles}>
                            <RangeFilter
                                id="budgetFilter"
                                getLabel={(value, defaultLabel) =>
                                    value.upper && parseInt(value.upper) < maxBudget
                                        ? `do ${formatBigNumbers(parseInt(value.upper))} ${getCurrency({
                                              currency: props.currency
                                          })}`
                                        : defaultLabel
                                }
                                label="Budżet"
                                isDropdownOpened={currentOpenFilter === DropdownToOpen.PRICE}
                                dropdownName={DropdownToOpen.PRICE}
                                labelId="budget-filter-field"
                                {...budgetProps}
                                onClearField={() => {
                                    onFieldClear(budgetProps.name);
                                }}
                                errorOnBottom
                                toggleDropdown={onDropdownOpen}
                                isMobileWidth={!!isMobile}
                            >
                                {(filterChildProps) => {
                                    return (
                                        <PropertyListFiltersBudget
                                            label={filterChildProps.label}
                                            onClear={() => {
                                                if (!isMobile) {
                                                    trackFilterClear({
                                                        eventName: "invest_filters",
                                                        filterType: "budget",
                                                        value: budgetProps.value,
                                                        currency: props.currency
                                                    });
                                                }
                                                // Clear field without triggering Remove event
                                                filterChildProps.onChange(filterChildProps.name, {lower: "", upper: ""});
                                                filterChildProps.onAfterChange?.(filterChildProps.name, {lower: "", upper: ""});
                                                // Reset form to default values
                                                reset(defaultBudgetValues);
                                                // Update preferences to clear budget
                                                updatePreferences({
                                                    price_1: undefined
                                                });
                                            }}
                                            closeForm={() => {
                                                filterChildProps.toggleDropdown();
                                            }}
                                            onSubmit={handleSubmit(onRHFSubmit)}
                                            maxPrice={maxBudget}
                                        />
                                    );
                                }}
                            </RangeFilter>
                        </FilterWrapper>

                        <FilterWrapper css={inputWrapperStyles}>
                            <RangeFilter
                                label="Pokoje"
                                id="roomFilter"
                                {...roomsProps}
                                onClearField={() => {
                                    onFieldClear(roomsProps.name);
                                }}
                                getLabel={getRoomLabel}
                                labelId="room-filter-field"
                                errorOnBottom
                                isDropdownOpened={currentOpenFilter === DropdownToOpen.ROOMS}
                                toggleDropdown={onDropdownOpen}
                                dropdownName={DropdownToOpen.ROOMS}
                                isMobileWidth={!!isMobile}
                                dataTestId={FILTERS_TEST_IDS.ROOMS}
                            >
                                {(props: IFilterChildProps<number | "">) => (
                                    <SearchFiltersRooms
                                        rooms={availableRooms}
                                        {...props}
                                        onSubmitSingle={() => {
                                            if (!isMobile) {
                                                trackFilterApply({
                                                    eventName: "invest_filters",
                                                    filterType: "rooms",
                                                    value: props.value
                                                });
                                            }

                                            updatePreferences({
                                                rooms_0: typeof props.value.lower === "number" ? props.value.lower.toString() : undefined,
                                                rooms_1: typeof props.value.upper === "number" ? props.value.upper.toString() : undefined
                                            });
                                        }}
                                        clearField={() => {
                                            if (!isMobile) {
                                                trackFilterClear({
                                                    eventName: "invest_filters",
                                                    filterType: "rooms",
                                                    value: props.value
                                                });
                                            }

                                            props.onChange(props.name, {lower: "", upper: ""});
                                            props.onAfterChange?.(props.name, {lower: "", upper: ""});
                                        }}
                                        showFooter={!isMobile}
                                    />
                                )}
                            </RangeFilter>
                        </FilterWrapper>

                        <FilterWrapper css={inputWrapperStyles}>
                            <RangeFilter
                                label="Metraż"
                                errorOnBottom
                                id="areaFilter"
                                {...areaProps}
                                onClearField={() => {
                                    onFieldClear(areaProps.name);
                                }}
                                onChange={areaProps.onChange}
                                getLabel={getAreaFilterLabel}
                                isDropdownOpened={currentOpenFilter === DropdownToOpen.AREA}
                                toggleDropdown={onDropdownOpen}
                                dropdownName={DropdownToOpen.AREA}
                                isMobileWidth={!!isMobile}
                                dataTestId={FILTERS_TEST_IDS.AREA_SIZE}
                            >
                                {(props: IFilterChildProps<string>) => (
                                    <>
                                        <Text variant="body_copy_1" as="p" mb={2}>
                                            {props.label}
                                        </Text>
                                        <InputRange
                                            {...props}
                                            error={areaErrorMessage}
                                            onChange={(name, value) => {
                                                const limitedValues = getAreaFilterLimitedValues(value as IRange<string>);
                                                props.onChange(name, limitedValues);
                                            }}
                                            hasDashSeparator
                                            roundToMultiplicationOf={Round.AREA}
                                        />

                                        {!isMobile && (
                                            <FilterFooter
                                                onCancel={() => {
                                                    trackFilterClear({
                                                        eventName: "invest_filters",
                                                        filterType: "area",
                                                        value: props.value
                                                    });

                                                    props.onChange(props.name, {lower: "", upper: ""});
                                                    props.onAfterChange?.(props.name, {lower: "", upper: ""});
                                                    props.toggleDropdown();
                                                }}
                                                onSave={() => {
                                                    if (!hasAreaValidationError(props.value)) {
                                                        trackFilterApply({
                                                            eventName: "invest_filters",
                                                            filterType: "area",
                                                            value: props.value
                                                        });

                                                        props.onChange(props.name, props.value);
                                                        props.onAfterChange?.(props.name, props.value);

                                                        updatePreferences({
                                                            area_0: props.value.lower !== "" ? props.value.lower : undefined,
                                                            area_1: props.value.upper !== "" ? props.value.upper : undefined
                                                        });

                                                        props.toggleDropdown();
                                                    }
                                                }}
                                            />
                                        )}
                                    </>
                                )}
                            </RangeFilter>
                        </FilterWrapper>

                        {shouldShowFloorsFilter && (
                            <FilterWrapper css={inputWrapperStyles}>
                                <Filter
                                    label="Piętra"
                                    id="floorChoicesFilter"
                                    {...(isHouseFilter ? houseStoreysProps : floorChoicesProps)}
                                    onClearField={() => {
                                        onFieldClear(isHouseFilter ? houseStoreysProps.name : floorChoicesProps.name);
                                    }}
                                    getLabel={(value) => getFloorChoiceLabel(value, offerType)}
                                    labelId={`${isHouseFilter ? "house_storeys" : "floor_choices"}-filter-field`}
                                    errorOnBottom
                                    isDropdownOpened={currentOpenFilter === DropdownToOpen.FLOORS}
                                    setDropdownOpen={onDropdownOpen}
                                    dropdownName={DropdownToOpen.FLOORS}
                                    isMobileWidth={!!isMobile}
                                    dataTestId={FILTERS_TEST_IDS.FLOORS}
                                >
                                    {(props) => (
                                        <>
                                            <FloorChoicesSwitch offerType={offerType} {...props} />

                                            {!isMobile && (
                                                <FilterFooter
                                                    onCancel={() => {
                                                        trackFilterClear({
                                                            eventName: "invest_filters",
                                                            filterType: "floor",
                                                            value: props.value,
                                                            offerType
                                                        });

                                                        props.onChange(props.name, []);
                                                        props.onAfterChange?.(props.name, []);

                                                        onDropdownOpen(null);
                                                    }}
                                                    onSave={() => {
                                                        const mappedValue = props.value.map((el) => el.toString());

                                                        trackFilterApply({
                                                            eventName: "invest_filters",
                                                            filterType: "floor",
                                                            value: props.value,
                                                            offerType
                                                        });

                                                        props.onChange(props.name, props.value);
                                                        props.onAfterChange?.(props.name, props.value);

                                                        updatePreferences(isHouseFilter ? {house_storeys: mappedValue} : {floor_choices: mappedValue});
                                                        onDropdownOpen(null);
                                                    }}
                                                />
                                            )}
                                        </>
                                    )}
                                </Filter>
                            </FilterWrapper>
                        )}
                    </div>

                    <div css={mobileFiltersFooterWrapper}>
                        <FilterFooter
                            onCancel={() => {
                                if (isMobile) {
                                    const {categories, options} = getMobileFiltersLabelForTracking(
                                        {
                                            price: budgetProps.value,
                                            rooms: roomsProps.value,
                                            area: areaProps.value,
                                            floor_choices: isHouseFilter ? undefined : floorChoicesProps.value,
                                            house_storeys: isHouseFilter ? houseStoreysProps.value : undefined
                                        },
                                        offerType,
                                        props.currency === Currency.EURO ? "eur" : "zł"
                                    );

                                    if (categories) {
                                        trackFilterClearForCategories({
                                            eventName: "invest_filters",
                                            filterCategory: categories as FilterCategory,
                                            customLabel: options || undefined
                                        });
                                    }
                                }

                                props.onMobileResetForm?.();
                                onFieldClear();
                            }}
                            onSave={(e) => {
                                if (isMobile) {
                                    const formValues = formMethods.getValues();
                                    const newBudget = formValues[BudgetFieldsNames.TOTAL];
                                    const newBudgetValue = {lower: "", upper: newBudget >= maxBudget ? "" : newBudget.toString()};

                                    handleSubmit(onRHFSubmit)(e);

                                    const {categories, options} = getMobileFiltersLabelForTracking(
                                        {
                                            price: newBudgetValue,
                                            rooms: roomsProps.value,
                                            area: areaProps.value,
                                            floor_choices: isHouseFilter ? undefined : floorChoicesProps.value,
                                            house_storeys: isHouseFilter ? houseStoreysProps.value : undefined
                                        },
                                        offerType,
                                        props.currency === Currency.EURO ? "eur" : "zł"
                                    );

                                    if (categories) {
                                        trackFilterApplyForCategories({
                                            eventName: "invest_filters",
                                            filterCategory: categories as FilterCategory,
                                            customLabel: options || undefined
                                        });
                                    }
                                } else {
                                    handleSubmit(onRHFSubmit)(e);
                                }
                            }}
                        />
                    </div>
                </form>
            </FormProvider>
        </FiltersHolder>
    );
};

export const PropertyListFilters = form<ISearchFiltersFormValues, IProps>(SearchFiltersC);

const FiltersHolder = styled.div<{shouldShowFloorsFilter: boolean}>`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 6rem);

    @media (min-width: ${(props) => props.theme.breakpoints.md}) {
        height: unset;
    }
`;

const filtersScroller = (theme: Theme) => css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: auto;

    @media (min-width: ${theme.breakpoints.md}) {
        flex-direction: row;
        overflow: initial;
    }
`;

const mobileFiltersFooterWrapper = (theme: Theme) => css`
    width: 100%;
    display: block;
    padding: 0 2rem 2rem;

    @media (min-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const inputWrapperStyles = css`
    padding: 0;
    margin-right: 0.8rem;

    ${onDesktop(css`
        flex: 1 1 20%;
    `)}
`;

const NoWrapper = styled.div`
    display: flex;
`;
